/**
 * Registry of all downhole data sources. Adding a new source means adding
 * one entry here plus a matching backend query -- no viewer code changes needed.
 */

export const CATEGORICAL_PALETTE = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
  '#911eb4', '#42d4f4', '#f032e6', '#bfef45', '#fabed4',
  '#469990', '#dcbeff', '#9A6324', '#fffac8', '#800000',
  '#aaffc3', '#808000', '#ffd8b1', '#000075', '#a9a9a9',
];

export const DOWNHOLE_SOURCES = {
  lithology: {
    label: 'Lith',
    colorType: 'categorical',
    colorField: 'rock_type',
    radiusField: null,
    radiusLabel: null,
    subTypeField: null,
    labelFormatter: (d) => d.rock_type || '?',
  },
  mag_sus: {
    label: 'MagSus',
    colorType: 'numeric',
    colorField: 'reading',
    radiusField: 'reading',
    radiusLabel: 'Mag Sus',
    subTypeField: null,
    labelFormatter: (d) => {
      const v = parseFloat(d.reading);
      return isNaN(v) ? '?' : v.toFixed(2);
    },
  },
  mineralization: {
    label: 'Mineral',
    colorType: 'categorical',
    colorField: 'mineral_type',
    radiusField: 'percent',
    radiusLabel: '% Mineral',
    subTypeField: 'mineral_type',
    labelFormatter: (d) => {
      const parts = [d.mineral_type || '?'];
      if (d.style) parts.push(d.style);
      const pct = parseFloat(d.percent);
      if (!isNaN(pct)) parts.push(`${pct}%`);
      return parts.join(' ');
    },
  },
  alteration: {
    label: 'Alter',
    colorType: 'categorical',
    colorField: 'alteration_type',
    radiusField: null,
    radiusLabel: null,
    subTypeField: 'alteration_type',
    labelFormatter: (d) => {
      const parts = [d.alteration_type || '?'];
      if (d.style) parts.push(d.style);
      return parts.join(' ');
    },
  },
};

export const SOURCE_IDS = Object.keys(DOWNHOLE_SOURCES);

/**
 * Build a legend object from interval data for a given source type.
 * @param {string} sourceId - Key in DOWNHOLE_SOURCES
 * @param {object[]} intervals - Raw interval rows for this type
 * @returns {object|null} Legend descriptor { type, label, mapping?, min?, max?, radiusInfo? }
 */
export function buildLegendForSource(sourceId, intervals) {
  const src = DOWNHOLE_SOURCES[sourceId];
  if (!src || !intervals?.length) return null;

  if (src.colorType === 'numeric') {
    const vals = intervals.map(d => parseFloat(d[src.colorField])).filter(v => !isNaN(v));
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const legend = { type: 'numeric', min, max, label: src.label, sourceId };
    if (src.radiusField) {
      legend.radiusInfo = { field: src.radiusField, min, max, label: src.radiusLabel };
    }
    return legend;
  }

  const keys = [...new Set(intervals.map(d => d[src.colorField]).filter(Boolean))];
  const mapping = {};
  keys.forEach((k, i) => {
    mapping[k] = CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length];
  });
  const legend = { type: 'categorical', mapping, label: src.label, sourceId };

  if (src.radiusField) {
    const vals = intervals.map(d => parseFloat(d[src.radiusField])).filter(v => !isNaN(v));
    if (vals.length > 0) {
      legend.radiusInfo = {
        field: src.radiusField,
        min: Math.min(...vals),
        max: Math.max(...vals),
        label: src.radiusLabel,
      };
    }
  }
  return legend;
}

/**
 * Resolve the color string (CSS) for an interval given its source legend.
 * @param {object} interval
 * @param {string} sourceId
 * @param {object} legend - From buildLegendForSource
 * @returns {string} CSS color
 */
export function getIntervalColor(interval, sourceId, legend) {
  if (!legend) return '#888888';
  const src = DOWNHOLE_SOURCES[sourceId];
  if (!src) return '#888888';

  if (legend.type === 'numeric') {
    const val = parseFloat(interval[src.colorField]);
    const t = legend.max > legend.min
      ? Math.max(0, Math.min(1, (val - legend.min) / (legend.max - legend.min)))
      : 0.5;
    const hue = (1 - t) * 240;
    return `hsl(${hue.toFixed(0)}, 90%, 50%)`;
  }

  return legend.mapping[interval[src.colorField]] || '#888888';
}

/**
 * Set a THREE.Color from an interval using sequential (numeric) or categorical mapping.
 * @param {THREE.Color} color - Mutable color object
 * @param {object} interval
 * @param {string} sourceId
 * @param {object} legend
 */
export function setIntervalThreeColor(color, interval, sourceId, legend) {
  if (!legend) { color.set('#888888'); return; }
  const src = DOWNHOLE_SOURCES[sourceId];
  if (!src) { color.set('#888888'); return; }

  if (legend.type === 'numeric') {
    const val = parseFloat(interval[src.colorField]);
    const t = legend.max > legend.min
      ? Math.max(0, Math.min(1, (val - legend.min) / (legend.max - legend.min)))
      : 0.5;
    color.setHSL((1 - t) * 0.667, 0.9, 0.5);
    return;
  }

  color.set(legend.mapping[interval[src.colorField]] || '#888888');
}

/**
 * Compute the scaled radius for an interval based on its legend radius info.
 * Returns a multiplier (1 = base, >1 = larger, <1 = smaller).
 */
export function getRadiusMultiplier(interval, legend, radiusScale) {
  if (radiusScale <= 0 || !legend?.radiusInfo) return 1;

  const val = parseFloat(interval[legend.radiusInfo.field]);
  if (isNaN(val)) return 1;

  const { min, max } = legend.radiusInfo;
  const normT = max > min ? Math.max(0, Math.min(1, (val - min) / (max - min))) : 0.5;
  const scaledR = 0.3 + 1.7 * normT;
  return 1 + radiusScale * (scaledR - 1);
}
